import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { DecisionItem, GateActions, ReviewCard, caseWorkflowService } from '../../services/caseWorkflowService';
import { hours } from '../CaseWorkflow/caseWorkflowVocabulary';
import { TierPill } from '../Benchmark/BenchmarkPrimitives';

interface DecisionListDrawerProps {
    gate: GateActions | null;
    onClose: () => void;
    /** A case's review card was located; the page opens it in the insight drawer. */
    onOpenCard: (card: ReviewCard, item: DecisionItem) => void;
}

/**
 * The cases waiting at one gate, oldest first, each opening the card it is
 * decided on.
 *
 * The list comes from the case store; the card lives in the insight store, so a
 * row resolves its card only when opened. A case whose card cannot be found says
 * so on its row rather than failing the list: the decision is still real, and
 * still listable through the review API.
 *
 * Docked from the same width as the insight drawer, so the page and its assistant
 * stay usable while the list is open.
 */
const DecisionListDrawer: React.FC<DecisionListDrawerProps> = ({ gate, onClose, onOpenCard }) => {
    const [opening, setOpening] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const open = async (item: DecisionItem) => {
        setOpening(item.threadId);
        setErrors((prev) => ({ ...prev, [item.threadId]: '' }));
        try {
            const card = await caseWorkflowService.getReviewCard(item.threadId);
            if (!card || !card.campaignId || !card.capsuleId || !card.groupIdentifier) {
                throw new Error('This review has no card to open.');
            }
            onOpenCard(card, item);
        } catch (error: any) {
            setErrors((prev) => ({ ...prev, [item.threadId]: error?.message || 'Could not open the review card.' }));
        } finally {
            setOpening(null);
        }
    };

    return (
        <EntityDetailDrawer
            open={Boolean(gate)}
            onClose={onClose}
            docked
            dockedMinWidth={1440}
            icon={<GavelIcon />}
            title={gate?.label ?? ''}
            subtitle={gate ? `${gate.openCases.toLocaleString()} ${gate.openCases === 1 ? 'case' : 'cases'} waiting` : undefined}
            description={gate?.question ?? ''}
        >
            {gate && (
                <ul className="list-none p-0 m-0 divide-y divide-line">
                    {gate.items.map((item) => (
                        <li key={item.threadId} className="py-3 text-left">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-fg truncate">
                                        {item.customerName || item.customerId}
                                    </div>
                                    <div className="text-[11px] text-fg-muted font-mono truncate">
                                        {item.caseId || item.customerId}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                        <span className="text-[11px] text-fg-2">Suggested</span>
                                        {item.suggestion && /^P[0-9]$/.test(item.suggestion)
                                            ? <TierPill code={item.suggestion} />
                                            : <span className="rounded-full border border-line px-2 py-0.5 text-[11px] text-fg-body">{item.suggestionLabel || '—'}</span>}
                                        {item.priority && /^P[0-9]$/.test(item.priority) && gate.reviewKind !== 'priority' && (
                                            <Tooltip title="The case's triage priority." arrow placement="top" describeChild>
                                                <span className="inline-flex"><TierPill code={item.priority} /></span>
                                            </Tooltip>
                                        )}
                                        {item.precedentDivergence && (
                                            <span className="text-[11px] text-fg-soft">· differs from comparable cases</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-fg-muted mt-1">
                                        Waiting {hours(item.ageHours)}
                                        {item.threads > 1 && ` · ${item.threads} pending threads, newest shown`}
                                    </div>
                                    {errors[item.threadId] && (
                                        <div className="text-[11px] text-[#FCA5A5] mt-1">{errors[item.threadId]}</div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => open(item)}
                                    disabled={opening === item.threadId}
                                    className="flex-none rounded-lg border border-accent-fg bg-transparent px-3 py-1.5 text-xs font-bold text-accent-fg hover:bg-accent/10 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                                >
                                    {opening === item.threadId ? 'Opening…' : 'Review'}
                                </button>
                            </div>
                        </li>
                    ))}
                    {gate.truncated && (
                        <li className="py-3 text-xs text-fg-muted">
                            Showing the {gate.items.length} longest-waiting of {gate.openCases.toLocaleString()} cases.
                        </li>
                    )}
                </ul>
            )}
        </EntityDetailDrawer>
    );
};

export default DecisionListDrawer;
